import { api } from "./client";

export type LabOrderStatus = "pending" | "in_progress" | "completed" | "cancelled";

export interface LabTestDefinition {
  code: string;
  name_ru: string;
  name_uz: string | null;
  name_en: string | null;
  unit: string | null;
  specimen: string | null;
  panel: string | null;
  accepted_text_values: string | null;
  is_active: boolean;
  normal_low_male?: number | null;
  normal_high_male?: number | null;
  normal_low_female?: number | null;
  normal_high_female?: number | null;
  normal_low_pregnancy?: number | null;
  normal_high_pregnancy?: number | null;
}

export interface LabResultRead {
  id: string;
  order_id: string;
  examination_id: string;
  test_code: string;
  value_numeric: number | null;
  value_text: string | null;
  unit: string | null;
  reference_range_low: number | null;
  reference_range_high: number | null;
  is_abnormal: boolean | null;
  entered_by_user_id: string;
  entered_at: string;
}

export interface LabOrderRead {
  id: string;
  examination_id: string;
  ordered_by_user_id: string;
  tests: string[];
  status: LabOrderStatus;
  completed_at: string | null;
  created_at: string;
}

export interface LabOrderDetail extends LabOrderRead {
  notes: string | null;
  results: LabResultRead[];
}

export interface LabOrderList {
  items: LabOrderRead[];
  total: number;
}

export interface LabOrderCreatePayload {
  tests: string[];
  notes?: string | null;
}

export interface LabResultEntry {
  test_code: string;
  value_numeric?: number | null;
  value_text?: string | null;
  unit?: string | null;
}

export async function listTestDefinitions(): Promise<LabTestDefinition[]> {
  const { data } = await api.get<LabTestDefinition[]>("/lab-tests");
  return data;
}

export interface LabTestCreatePayload {
  code: string;
  name_ru: string;
  name_uz?: string | null;
  name_en?: string | null;
  unit?: string | null;
  specimen?: string | null;
  panel?: string | null;
  normal_low_male?: number | null;
  normal_high_male?: number | null;
  normal_low_female?: number | null;
  normal_high_female?: number | null;
  normal_low_pregnancy?: number | null;
  normal_high_pregnancy?: number | null;
}

export async function createLabTestDefinition(
  payload: LabTestCreatePayload,
): Promise<LabTestDefinition> {
  const { data } = await api.post<LabTestDefinition>("/references/lab-tests", payload);
  return data;
}

export async function updateLabTestDefinition(
  code: string,
  payload: Partial<Omit<LabTestCreatePayload, "code">>,
): Promise<LabTestDefinition> {
  const { data } = await api.patch<LabTestDefinition>(
    `/references/lab-tests/${encodeURIComponent(code)}`,
    payload,
  );
  return data;
}

export async function deleteLabTestDefinition(code: string): Promise<void> {
  await api.delete(`/references/lab-tests/${encodeURIComponent(code)}`);
}

export async function downloadLabOrderPdf(orderId: string, lang: string): Promise<Blob> {
  const { data } = await api.get<Blob>(`/lab-orders/${orderId}/pdf`, {
    params: { lang },
    responseType: "blob",
  });
  return data;
}

export async function createLabOrder(
  examId: string,
  payload: LabOrderCreatePayload,
): Promise<LabOrderRead> {
  const { data } = await api.post<LabOrderRead>(
    `/examinations/${examId}/lab-orders`,
    payload,
  );
  return data;
}

export async function listLabOrders(params: {
  status?: LabOrderStatus;
  limit?: number;
}): Promise<LabOrderList> {
  const { data } = await api.get<LabOrderList>("/lab-orders", { params });
  return data;
}

export async function getLabOrder(id: string): Promise<LabOrderDetail> {
  const { data } = await api.get<LabOrderDetail>(`/lab-orders/${id}`);
  return data;
}

export async function submitLabResults(
  orderId: string,
  results: LabResultEntry[],
): Promise<LabResultRead[]> {
  const { data } = await api.post<LabResultRead[]>(
    `/lab-orders/${orderId}/results`,
    { results },
  );
  return data;
}

// ─── Lab templates (Word-uploaded result sheets) ────────────────────────
export interface LabTemplate {
  id: string;
  clinic_id: string;
  name: string;
  description: string | null;
  headers: string[];
  data_rows: string[][];
  result_col_index: number | null;
  created_at: string;
}

export async function listLabTemplates(): Promise<LabTemplate[]> {
  const { data } = await api.get<{ items: LabTemplate[]; total: number }>(
    "/lab-templates",
  );
  return data.items;
}

export async function getLabTemplate(id: string): Promise<LabTemplate> {
  const { data } = await api.get<LabTemplate>(`/lab-templates/${id}`);
  return data;
}

export async function uploadLabTemplate(
  name: string,
  file: File,
  description?: string,
): Promise<LabTemplate> {
  const fd = new FormData();
  fd.append("name", name);
  if (description) fd.append("description", description);
  fd.append("file", file);
  const { data } = await api.post<LabTemplate>("/lab-templates", fd, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return data;
}

export async function deleteLabTemplate(id: string): Promise<void> {
  await api.delete(`/lab-templates/${id}`);
}
